import React, { ReactNode, useContext, useState } from "react";
import { AuthModel } from "../api/auth/AuthModel";
import { AppConfigContext } from "./AppConfigContext";
import { LoginResponse } from "../api/auth/Auth.typed";
import { useAlert } from "../hooks/useAlert";

interface AuthProviderProps {
  children: ReactNode;
}

interface AuthContextType {
  login: (username: string, password: string) => Promise<void>;
  saveToken: (token: string) => void;
  getToken: () => string | null;
  logout: () => void;
  logoutUnauthorized: () => void;
  isAuthenticated: boolean;
}

export const AuthContext = React.createContext<AuthContextType>(null!);

export function AuthProvider({ children }: AuthProviderProps) {
  const appConfig = useContext(AppConfigContext);
  const { showAlert } = useAlert();
  const authModel = new AuthModel(appConfig!);

  const [token, setToken] = useState<string | null>(
    () => localStorage.getItem("authToken") || null,
  );

  const saveToken = (newToken: string) => {
    setToken(newToken);
    localStorage.setItem("authToken", newToken);
  };

  const getToken = () => token;

  const login = async (username: string, password: string) => {
    try {
      const response: LoginResponse = await authModel.login(username, password);
      saveToken(response.response.token);
      showAlert("Connexion réussie", "success");
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem("authToken");
    showAlert("Déconnexion réussie", "success");
  };

  const logoutUnauthorized = () => {
    setToken(null);
    localStorage.removeItem("authToken");
    showAlert("Session expirée. Redirection vers la page de connexion.", "danger");
  };

  const isAuthenticated = Boolean(token);

  const contextValue = {
    login,
    saveToken,
    getToken,
    logout,
    logoutUnauthorized,
    isAuthenticated,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
